import { useState, useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { theme } from './utility/theme';
import { ThemeProvider, CssBaseline } from '@mui/material';
import toast, { Toaster } from 'react-hot-toast';
import Router from './Router';
import FloatingBtn from './component/register/AnalysisStatusFloatingBar';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { useTranslation } from './hook/useTranslation';
import { useAnalysisStore } from './store/useAnalysisStore';
import { reportApi } from './assets/api/apis';
import {
  ACCESS_TOKEN_KEY,
  ANALYSIS_STATUS,
  ON_KIDS_ANALYSIS_THUMBNAIL,
  ON_KIDS_ANALYSIS_TYPE,
} from './data/define';
import './App.css';

function AppContent() {
  const { t } = useTranslation();

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 1000 * 60,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
          },
        },
        queryCache: new QueryCache({
          onError: (error, query) => {
            if (query.getObserversCount() === 0) return;
            if (query.state.data !== undefined) return;
            if (query.meta?.errorMessage) {
              console.error(`[Query] ${query.meta.errorMessage}`, error);
              toast.error(t(query.meta.errorMessage as string));
            }
          },
        }),
        mutationCache: new MutationCache({
          onError: (_error, _variables, _context, mutation) => {
            if (mutation.meta?.errorMessage) {
              toast.error(t(mutation.meta.errorMessage as string));
            }
          },
        }),
      })
  );

  return (
    <QueryClientProvider client={queryClient}>
      <CssBaseline />
      <Toaster />
      <Router />
      <FloatingBtn />
    </QueryClientProvider>
  );
}

function App() {
  useEffect(() => {
    const restoreActiveJob = async () => {
      if (useAnalysisStore.getState().analysisResult.status !== ANALYSIS_STATUS.STANDBY) return;
      if (!localStorage.getItem(ACCESS_TOKEN_KEY)) return;
      try {
        const res = await reportApi.getActiveAiJobs();
        const jobs = res.data;
        if (!jobs || jobs.length === 0) return;
        const job = jobs[0];

        // 썸네일 복원
        const thumbnail = sessionStorage.getItem(ON_KIDS_ANALYSIS_THUMBNAIL);
        if (thumbnail) {
          useAnalysisStore.getState().setSavedThumbnail({ thumbnail, origCoords: null });
        }

        useAnalysisStore.getState().setAiJobId(job.id);
        useAnalysisStore.getState().setAnalysisResult({
          status: ANALYSIS_STATUS.PENDING,
          data: null,
          error: null,
          info: {
            kidId: job.kidId,
            reportId: job.reportId,
            type: localStorage.getItem(ON_KIDS_ANALYSIS_TYPE) || undefined,
            mi: null,
          },
        });
      } catch (error) {
        // 비로그인 상태 등 — 무시
      }
    };
    restoreActiveJob();
  }, []);

  return (
    <BrowserRouter>
      <ThemeProvider theme={theme}>
        <AppContent />
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
